/**
 * The header's context picker — "which period, which account".
 *
 * Wallet puts the account selector in the app bar because the answer to every
 * number on screen depends on it. AIM has both an account dimension AND a
 * period dimension (`lib/periods.ts` already defines the five the rest of the
 * app uses), so both live here, in one sheet, opened by one tap on the header.
 *
 * It changes SHELL STATE only. No route, no query param, no fetch of its own —
 * the accounts list is handed down from the shell, which loaded it once.
 */
import { Check, Wallet } from "lucide-react";
import { cn } from "@/lib/utils";
import { getPeriodBounds, type PeriodKey } from "@/lib/periods";
import { Sheet } from "./Sheet";
import { accountLabel, useWalletShell } from "./context";

const PERIODS: { key: PeriodKey; label: string }[] = [
  { key: "weekly", label: "Week" },
  { key: "monthly", label: "Month" },
  { key: "quarterly", label: "Quarter" },
  { key: "halfyear", label: "Half year" },
  { key: "fy", label: "Financial year" },
];

export function ContextSheet({ open, onClose }: { open: boolean; onClose(): void }) {
  const { period, setPeriod, accountId, setAccountId, accounts } = useWalletShell();

  const rowCls = (active: boolean) =>
    cn(
      "flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition-colors",
      active
        ? "bg-[var(--cp-accent-wash)] text-[var(--cp-accent-ink)]"
        : "text-[var(--cp-ink-2)] hover:bg-[var(--cp-surface-2)]",
    );

  return (
    <Sheet open={open} onClose={onClose} title="Period and account">
      <div className="pb-2">
        <p className="cp-micro-label px-4 pb-1 pt-3">Period</p>
        {PERIODS.map((option) => {
          const active = option.key === period;
          return (
            <button
              key={option.key}
              type="button"
              className={rowCls(active)}
              onClick={() => {
                setPeriod(option.key);
                onClose();
              }}
            >
              <span className="flex-1">{option.label}</span>
              <span className="text-xs text-[var(--cp-ink-3)]">
                {getPeriodBounds(option.key).label}
              </span>
              {active && <Check className="h-4 w-4 shrink-0" />}
            </button>
          );
        })}

        <div className="my-2 border-t border-[var(--cp-line-2)]" />

        <p className="cp-micro-label px-4 pb-1 pt-1">Account</p>
        <button
          type="button"
          className={rowCls(accountId === null)}
          onClick={() => {
            setAccountId(null);
            onClose();
          }}
        >
          <Wallet className="h-4 w-4 shrink-0 text-[var(--cp-ink-3)]" />
          <span className="flex-1">All accounts</span>
          {accountId === null && <Check className="h-4 w-4 shrink-0" />}
        </button>
        {accounts.map((account) => {
          const active = account.id === accountId;
          return (
            <button
              key={account.id}
              type="button"
              className={rowCls(active)}
              onClick={() => {
                setAccountId(account.id);
                onClose();
              }}
            >
              <span
                aria-hidden="true"
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ background: account.color ?? "var(--cp-accent)" }}
              />
              <span className="min-w-0 flex-1 truncate">{accountLabel(account)}</span>
              {active && <Check className="h-4 w-4 shrink-0" />}
            </button>
          );
        })}
        {accounts.length === 0 && (
          <p className="px-4 py-3 text-sm text-[var(--cp-ink-3)]">
            No accounts yet — every amount is shown across all of them.
          </p>
        )}
      </div>
    </Sheet>
  );
}
